import { ReactNode } from "react";
import { useAuth } from "../lib/AuthContext";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Alert, AlertDescription } from "./ui/alert";
import { 
  LayoutDashboard, 
  ShieldCheck, 
  Building2, 
  Package, 
  MessageSquare,
  AlertCircle,
  Home
} from "lucide-react";
import { DebugBanner } from "./DebugBanner";
import { useFeatureFlags } from "../lib/FeatureFlagsContext";

interface AdminLayoutProps {
  children: ReactNode;
  currentPage: string;
  onNavigate: (page: string) => void;
}

/**
 * Admin Layout Component
 * Provides consistent navigation and layout for admin pages
 */
export function AdminLayout({ children, currentPage, onNavigate }: AdminLayoutProps) {
  const { profile } = useAuth();
  const { isEnabled } = useFeatureFlags();

  // Check if user is admin
  if (!profile || profile.role !== 'admin') {
    return (
      <div className="min-h-screen bg-background pt-24 pb-16">
        <div className="container mx-auto px-4">
          <Alert variant="destructive">
            <AlertCircle className="w-4 h-4" />
            <AlertDescription>
              Access denied. This area is only accessible to admin accounts.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  const navItems = [
    {
      id: 'admin',
      label: 'Dashboard',
      icon: LayoutDashboard,
      description: 'Overview & stats',
      enabled: true,
    },
    {
      id: 'admin-verify',
      label: 'Verify',
      icon: ShieldCheck,
      description: 'Patient verification',
      enabled: isEnabled('adminVerify'),
    },
    {
      id: 'admin-org',
      label: 'Org',
      icon: Building2,
      description: 'Clinics & pharmacies',
      enabled: isEnabled('adminOrg'),
    },
    {
      id: 'admin-catalog',
      label: 'Catalogue',
      icon: Package,
      description: 'Products & batches',
      enabled: isEnabled('adminCatalog'),
    },
    {
      id: 'admin-responses',
      label: 'Responses',
      icon: MessageSquare,
      description: 'Reviews & moderation',
      enabled: isEnabled('adminResponses'),
    },
  ];

  const visibleNavItems = navItems.filter(item => item.enabled);

  return (
    <div className="min-h-screen bg-background">
      {/* Debug Banner */}
      <DebugBanner />

      {/* Top Navigation Bar */}
      <div className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo & Badge */}
            <div className="flex items-center gap-4">
              <button 
                onClick={() => onNavigate('home')}
                className="flex items-center gap-2 hover:opacity-80 transition-opacity"
              >
                <Home className="w-5 h-5 text-primary" />
                <span className="font-semibold">CannaBias</span>
              </button>
              <Badge variant="default" className="bg-primary">
                🔑 Admin
              </Badge>
            </div>

            {/* Navigation Tabs */}
            <nav className="hidden md:flex items-center gap-2">
              {visibleNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = currentPage === item.id;

                return (
                  <button
                    key={item.id}
                    onClick={() => onNavigate(item.id)}
                    className={`
                      flex items-center gap-2 px-4 py-2 rounded-lg transition-all
                      ${isActive 
                        ? 'bg-primary text-primary-foreground shadow-sm' 
                        : 'hover:bg-accent text-muted-foreground hover:text-foreground'
                      }
                    `}
                    title={item.description}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="font-medium">{item.label}</span>
                  </button>
                );
              })}
            </nav>

            {/* User Info */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground hidden lg:inline">
                {profile.display_name}
              </span>
            </div>
          </div>

          {/* Mobile Navigation */}
          <div className="md:hidden pb-3 flex gap-2 overflow-x-auto">
            {visibleNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = currentPage === item.id;

              return (
                <button
                  key={item.id}
                  onClick={() => onNavigate(item.id)}
                  className={`
                    flex items-center gap-2 px-3 py-1.5 rounded-lg whitespace-nowrap transition-all flex-shrink-0
                    ${isActive 
                      ? 'bg-primary text-primary-foreground' 
                      : 'bg-muted text-muted-foreground'
                    }
                  `}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-sm font-medium">{item.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Page Content */}
      <main className="container mx-auto px-4 py-8">
        {children}
      </main>
    </div>
  );
}
